// ITEM A — **HER SIX SECTIONS, AND ADVANCED AT THE BOTTOM.**
//
// Her order: *corporate, easy to use, easy to understand, not complicated. Six top-level sections
// and nothing else* — Overview, Members, Financials, Moderation, IT Tickets, Grants — with the deep
// diagnostic views under ONE collapsed **Advanced** area at the bottom of the menu, and **nothing
// deleted underneath**: every surface that existed still exists and still loads.
//
// **WHY THIS IS TWO ROWS AND NOT A LONGER TAB STRIP.** D6-3 had ONE flat list of ten keys doing two
// different jobs — a door on her menu and a surface that renders — and the moment three surfaces
// (`triage`, `catalogs`, `review`) needed to exist without earning a top-level door, they were typed
// as LITERALS at the render site. So the strip she read never described what the dashboard could
// show. `SECTIONS` are the doors, `VIEWS` are the surfaces, and the fixture holds their union as a
// biconditional (`rosterViolations` in src/commandCenter.js): a view in no section is a surface with
// no door, and a view in two is two answers to where she acts on it.
//
// **A SECTION WITH ONE VIEW DRAWS NO SECOND ROW.** Four of her six hold exactly one surface, and a
// sub-nav with a single tab in it is a control that decides nothing — which is the clutter her *not
// complicated* clause is about. The row appears only where there is a choice.
//
// **ADVANCED IS COLLAPSED AND SAYS WHY IT IS SAFE TO LEAVE CLOSED**: *nothing in here is waiting on
// you.* It opens by itself when a tap lands inside it (`goToView` answers `openAdvanced`), because a
// destination that silently does nothing is worse than no link at all.
//
// Hookless — the open/closed flag is state on the page, so a gate can CALL this and read the tree
// back (R7's entry requirement).
import { useEffect, useState } from "react";
import { ChevronDown, ChevronRight } from "lucide-react";
import { loadOwnerData, applicationsBadge, APPLICATIONS_OPEN_PATH, type OwnerAnswer } from "../lib/owner-load";
import { SECTIONS, viewsOf, sectionNote } from "../commandCenter.js";

export function OwnerNav({
  section, view, advancedOpen, onSection, onView, onToggleAdvanced,
}: {
  section: string;
  view: string;
  advancedOpen: boolean;
  onSection: (key: string) => void;
  onView: (key: string) => void;
  onToggleAdvanced: () => void;
}) {
  const plain = SECTIONS.filter((s) => !s.advanced);
  const advanced = SECTIONS.find((s) => s.advanced) || null;
  const views = viewsOf(section);
  // ITEM 7b — her advisor applications, one tap from the top bar, with the count of the ones she has
  // not answered. ITEM 7 GATE REPAIR: the answer is kept WHOLE and read by `applicationsBadge`
  // (R65's three states: waiting, a real count, a read that failed and says so), and the link is an
  // owner route that redirects her seat server-side, so no console address ships in the bundle.
  const [appsAnswer, setAppsAnswer] = useState<OwnerAnswer<{ fresh?: unknown }> | null>(null);
  useEffect(() => {
    let live = true;
    void loadOwnerData<{ fresh?: unknown }>("/api/owner/command/applications").then((a) => {
      if (live) setAppsAnswer(a);
    });
    return () => { live = false; };
  }, []);
  const apps = applicationsBadge(appsAnswer);

  return (
    <>
      <div className="owner-sections" role="tablist" aria-label="Sections">
        {plain.map((s) => (
          <span key={s.key} className="owner-sections__slot">
            <button role="tab" type="button" aria-selected={section === s.key} title={sectionNote(s.key)}
              className={section === s.key ? "owner-section is-active" : "owner-section"}
              onClick={() => onSection(s.key)}>
              {s.label}
            </button>
            {/* ITEM 7b — right after IT Tickets, her word. A LINK, not a tab: the applications live on
                the Truth console, and this opens it at them. */}
            {s.key === "tickets" ? (
              <a className="owner-section owner-section--apps" href={APPLICATIONS_OPEN_PATH}
                title={apps.kind === "failed" ? apps.note : undefined}
                aria-label={apps.kind === "count" && apps.fresh ? `Advisor Applications, ${apps.fresh} new`
                  : apps.kind === "failed" ? `Advisor Applications. ${apps.note}` : "Advisor Applications"}>
                Advisor Applications
                {apps.kind === "count" && apps.fresh ? <span className="owner-section__count">{apps.fresh}</span> : null}
                {apps.kind === "failed" ? <span className="owner-section__count owner-section__count--failed" role="status" title={apps.note}>!</span> : null}
              </a>
            ) : null}
          </span>
        ))}
        {advanced ? (
          <button type="button" className={section === advanced.key ? "owner-section owner-section--adv is-active" : "owner-section owner-section--adv"}
            aria-expanded={advancedOpen} title={sectionNote(advanced.key)} onClick={onToggleAdvanced}>
            {advancedOpen ? <ChevronDown size={14} /> : <ChevronRight size={14} />}{advanced.label}
          </button>
        ) : null}
      </div>

      {/* THE DEEP VIEWS, ONLY WHEN SHE HAS OPENED THEM. Her sentence puts them at the bottom of the
          menu, so they hang under the section row rather than becoming a seventh door. */}
      {advanced && advancedOpen ? (
        <div className="owner-views owner-views--adv" role="tablist" aria-label={advanced.label}>
          {viewsOf(advanced.key).map((v) => (
            <button key={v.key} role="tab" type="button" aria-selected={view === v.key} title={v.note}
              className={view === v.key ? "owner-view is-active" : "owner-view"}
              onClick={() => onView(v.key)}>
              {v.label}
            </button>
          ))}
        </div>
      ) : null}

      {/* A section's own surfaces. One view means no choice to make, so no row is drawn. */}
      {/* ITEM 7c — not a second time: with Advanced open, its views are already drawn just above. */}
      {views.length > 1 && !(advanced && advancedOpen && section === advanced.key) ? (
        <div className="owner-views" role="tablist" aria-label="Views">
          {views.map((v) => (
            <button key={v.key} role="tab" type="button" aria-selected={view === v.key} title={v.note}
              className={view === v.key ? "owner-view is-active" : "owner-view"}
              onClick={() => onView(v.key)}>
              {v.label}
            </button>
          ))}
        </div>
      ) : null}
    </>
  );
}
